// assets/tufted-text-preview.js

const FIELD_SELECTOR = 'input[type="text"], input:not([type]), textarea';

/**
 * Attaches a live preview line under every customer text field in the
 * surrounding product section, rendered in the product's tufting font.
 * Works with fields rendered later by apps (e.g. Qikify) via a MutationObserver.
 */
class TuftedTextPreview extends HTMLElement {
  /** @type {Map<HTMLInputElement | HTMLTextAreaElement, HTMLElement>} */
  #previews = new Map();

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #scanFrame = null;

  connectedCallback() {
    this.scope = this.closest('.shopify-section') || document.body;
    this.fieldFilters = (this.dataset.fields || '')
      .split('|')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);

    this.#scan();
    this.#observer = new MutationObserver(() => this.#scheduleScan());
    this.#observer.observe(this.scope, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    if (this.#scanFrame) cancelAnimationFrame(this.#scanFrame);
    for (const [field, preview] of this.#previews) {
      field.removeEventListener('input', this.#handleInput);
      preview.remove();
    }
    this.#previews.clear();
  }

  #scheduleScan() {
    if (this.#scanFrame) return;
    this.#scanFrame = requestAnimationFrame(() => {
      this.#scanFrame = null;
      this.#scan();
    });
  }

  #scan() {
    // Drop previews whose field was removed (apps can re-render their forms).
    for (const [field, preview] of this.#previews) {
      if (!field.isConnected) {
        preview.remove();
        this.#previews.delete(field);
      }
    }

    for (const field of this.scope.querySelectorAll(FIELD_SELECTOR)) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      if (this.#previews.has(field) || !this.#isCustomerTextField(field)) continue;
      this.#attach(field);
    }
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   */
  #isCustomerTextField(field) {
    if (field.disabled || field.readOnly) return false;
    if (field.closest('gift-card-recipient-form, search-component, predictive-search-component, form[action*="/search"]')) {
      return false;
    }
    if (this.fieldFilters.length === 0) return true;

    const label = this.#getFieldLabel(field);
    return this.fieldFilters.some((filter) => label.includes(filter));
  }

  /**
   * Builds a lowercase string from everything that might name the field,
   * since app-rendered markup does not always use <label for>.
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   */
  #getFieldLabel(field) {
    const parts = [
      field.name,
      field.placeholder,
      field.getAttribute('aria-label'),
      ...Array.from(field.labels || [], (label) => label.textContent),
    ];

    const labelledBy = field.getAttribute('aria-labelledby');
    if (labelledBy) {
      for (const id of labelledBy.split(/\s+/)) parts.push(document.getElementById(id)?.textContent);
    }

    // Nearest label-like element in the surrounding few wrappers.
    let ancestor = field.parentElement;
    for (let depth = 0; ancestor && ancestor !== this.scope && depth < 3; depth++) {
      const labelElement = ancestor.querySelector('label, legend, [class*="label" i], [class*="title" i]');
      if (labelElement && !labelElement.contains(field)) {
        parts.push(labelElement.textContent);
        break;
      }
      ancestor = ancestor.parentElement;
    }

    return parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').toLowerCase();
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   */
  #attach(field) {
    const preview = document.createElement('div');
    preview.className = 'tufted-text-preview__box';
    preview.setAttribute('aria-hidden', 'true');
    // Carry over the block's color/size settings (CSS custom properties).
    preview.setAttribute('style', this.getAttribute('style') || '');

    if (this.dataset.label) {
      const label = document.createElement('span');
      label.className = 'tufted-text-preview__label';
      label.textContent = this.dataset.label;
      preview.append(label);
    }

    const text = document.createElement('span');
    text.className = 'tufted-text-preview__text';
    text.style.fontFamily = `'${this.dataset.fontFamily}', cursive`;
    preview.append(text);

    // Place the preview after the field's own wrapper (which usually also holds
    // the character counter) so it sits underneath the whole field. If that
    // wrapper holds other fields too, place it directly after the field.
    const wrapper = field.parentElement;
    const ownsWrapper =
      wrapper && wrapper !== this.scope && wrapper.querySelectorAll(FIELD_SELECTOR).length === 1;
    (ownsWrapper ? wrapper : field).after(preview);

    this.#previews.set(field, preview);
    field.addEventListener('input', this.#handleInput);
    this.#render(field);
  }

  /**
   * @param {Event} event
   */
  #handleInput = (event) => {
    const field = event.currentTarget;
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) this.#render(field);
  };

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement} field
   */
  #render(field) {
    const preview = this.#previews.get(field);
    const text = preview?.querySelector('.tufted-text-preview__text');
    if (!preview || !text) return;

    const isEmpty = field.value.trim() === '';
    const placeholder = this.dataset.placeholder || '';
    text.textContent = isEmpty ? placeholder : field.value;
    text.classList.toggle('is-placeholder', isEmpty);
    preview.hidden = isEmpty && !placeholder;
  }
}

if (!customElements.get('tufted-text-preview')) {
  customElements.define('tufted-text-preview', TuftedTextPreview);
}
